import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';

export interface StockPayload {
  name: string;
  type: string;
  totalQty: number | string; // allow component to pass either numeric or formatted string
  description?: string;
  price: number | string;
  files: File[];
}

export interface StockEditPayload {
  id: number;
  name: string;
  type: string;
  totalQty: number | string;
  description?: string;
  price: number | string;
  files?: File[];
}

export interface StockPlanPayload {
  id: number | null;
  title: string;
  warehouseName: string;
  description?: string;
  items: Array<{
    id: number | null;
    name: string;
    type?: string;
    unit?: string;
    quantity: number | string;
    retailPrice: number | string;
    soldQty?: number | string;
    isActive?: boolean;
    wholesaleTotal: number | string;
    note?: string;
    revenue?: number | string;
    profit?: number | string;
    existingImages?: string[];
    fileIds?: number[];
  }>;
  totalRevenue?: number | string;
  totalWholesale?: number | string;
  totalProfit?: number | string;
}

export interface StockPaymentPayload {
  stockId: number;
  quantity: number | string;
}

export interface SearchBigDecimal {
  fromNumber?: number | null;
  toNumber?: number | null;
}

export interface SearchStockInDto {
  keyword?: string | null;
  price?: SearchBigDecimal | null;
  sortType?: 'ASC' | 'DESC' | null;
}

@Injectable({
  providedIn: 'root'
})
export class StockService {
  private readonly createEndpoint = 'stock/new';
  private readonly editEndpoint = 'stock/edit';
  private readonly deleteEndpoint = 'stock/deleteStock';
  private readonly paymentEndpoint = 'buy/payment';

  // Endpoint lấy chi tiết kho theo id
  private readonly detailEndpoint = 'stock';

  constructor(private api: ApiService) { }

  createStock(payload: StockPayload): Observable<any> {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('type', payload.type);
    formData.append('totalQty', payload.totalQty.toString());
    formData.append('price', payload.price.toString());

    if (payload.description) {
      formData.append('description', payload.description);
    }

    if (payload.files?.length) {
      payload.files.forEach(file => {
        formData.append('files', file, file.name);
      });
    }

    return this.api.postFormData(this.createEndpoint, formData);
  }

  editStock(payload: StockEditPayload | StockPlanPayload): Observable<any> {
    if ('items' in payload) {
      return this.api.post<any>(this.editEndpoint, payload);
    }

    const formData = new FormData();
    formData.append('id', payload.id.toString());
    formData.append('name', payload.name);
    formData.append('type', payload.type);
    formData.append('totalQty', payload.totalQty.toString());
    formData.append('price', payload.price.toString());

    if (payload.description) {
      formData.append('description', payload.description);
    }

    if (payload.files?.length) {
      payload.files.forEach(file => {
        formData.append('files', file, file.name);
      });
    }

    return this.api.postFormData(this.editEndpoint, formData);
  }

  /** Lấy chi tiết 1 kho theo id */
  getStock(id: number): Observable<any> {
    return this.api.get<any>(`${this.detailEndpoint}/detail/${id}`);
  }

  /** Danh sách chi tiết kho với filter theo StockItemInDto */
  listDetail(inDto: any): Observable<any> {
    return this.api.post<any>(`${this.detailEndpoint}/listDetail`, inDto);
  }

 getMyDetail(id: number, userId?: number | null): Observable<any> {
  return this.api.get<any>(
    `${this.detailEndpoint}/getMyDetail/${id}`,
    {
      params: {
        userId: userId != null ? userId.toString() : ''
      }
    }
  );
}

  /** Danh sách kho + chi tiết (items) */
  getAllStockDetails(userId?: number | null): Observable<any> {
    const params = userId === null || userId === undefined ? undefined : { id: String(userId) };
    return this.api.get<any>(`${this.detailEndpoint}/listDetail`, params);
  }

  /** Chi tiết kho theo userId: GET stock/detailByUserId/{userId} */
  getStockDetailByUserId(userId: number): Observable<any> {
    return this.api.get<any>(`${this.detailEndpoint}/detailByUserId/${userId}`);
  }

  deleteStock(id: number): Observable<any> {
    return this.api.post<any>(`${this.deleteEndpoint}/${id}`, {});
  }

  payment(payload: StockPaymentPayload): Observable<any> {
    return this.api.post<any>(this.paymentEndpoint, {
      stockId: payload.stockId,
      quantity: payload.quantity,
    });
  }

  searchStocks(payload: SearchStockInDto): Observable<any> {
    return this.api.post<any>(`${this.detailEndpoint}/search`, payload);
  }

  regisContract(data: any): Observable<any> {
    return this.api.post<any>(`${this.detailEndpoint}/register-contract`, data);
  }
  contract(data: any): Observable<any> {
    return this.api.post<any>('contract/add', data);
  }
}
